import React, { useContext, useMemo, useCallback } from 'react';
import { AppContext, IAppContext } from 'appState/appContext';
import { IFileData, TFileExtension } from 'types';
import { useBlobDownload } from 'hooks/useBlobDownload';
import { regexForStartLinks } from 'appConstants';
import { Rule } from 'antd/lib/form';
import { useTranslation } from 'react-i18next';
import { createAppError } from 'utils/helpers';
import { postMediaFile } from 'backend/additionalInformation';
import Text from 'components/Text/Text';
import TooltipForElement from 'components/Tooltips/TooltipForElement';
import FileUploader from 'components/UploaderAndDownloader/FileUploader';
import BlobDownloader from 'components/UploaderAndDownloader/BlobDownloader';
import DragAndDropForFile from 'components/DragAndDropForFile/DragAndDropForFile';
import FormInput from 'components/Input/FormInput';
import breadcrumbs from 'assets/img/breadcrumbs.svg';
import classNames from 'classnames';
import style from './BillTemplateBlock.module.scss';

const allowedExtensions: TFileExtension[] = ['png', 'jpg', 'jpeg'];

interface IBillTemplateBlock {
	changeBillTemplateFileData: (value: IFileData) => void;
	changeBillAdvertisingImageData: (value: IFileData) => void;
	changeBillAdvertisingLink: (value: string) => void;
	onImageFileSizeExceeded: () => void;
	onHtmlFileSizeExceeded: () => void;
	onDeleteBillAdvertisingImage: () => void;
	billTemplateFilePath?: Nullable<string>;
	billTemplateFileData?: IFileData;
	billAdvertisingImageData: IFileData;
	billAdvertisingImage?: Nullable<string>;
}

const BillTemplateBlock: React.FC<IBillTemplateBlock> = ({
	changeBillTemplateFileData,
	changeBillAdvertisingImageData,
	changeBillAdvertisingLink,
	onImageFileSizeExceeded,
	onHtmlFileSizeExceeded,
	onDeleteBillAdvertisingImage,
	billTemplateFilePath,
	billTemplateFileData,
	billAdvertisingImageData,
	billAdvertisingImage,
}) => {
	const { addAppError, showLoader, hideLoader } = useContext<IAppContext>(AppContext);
	const blobDownload = useBlobDownload({
		filePath: billAdvertisingImage || '',
		fileExtension: billAdvertisingImage?.split('.').pop() as TFileExtension, // TODO rewrite later
		fileName: 'image',
	});
	const downloadIconClasses = classNames(style.icon, style.downloadIcon, { [style.disabled]: !billAdvertisingImage });
	const deleteIconClasses = classNames(style.icon, style.deleteIcon, {
		[style.disabled]: !billAdvertisingImage && !billAdvertisingImageData.fileId,
	});

	const { t: tForm } = useTranslation('form');
	const { t: tError } = useTranslation('error');
	const validationRules = useMemo<Rule[]>(
		() => [
			() => ({
				validator(_, value) {
					if (value && !value.match(regexForStartLinks)) {
						return Promise.reject(new Error(tError('linkMustStartWithHttp')));
					}
					return Promise.resolve();
				},
			}),
		],
		[tError],
	);

	const handleHtmlUpload = useCallback(
		async (htmlFile: File, callback: (value: string) => void) => {
			const maxFileSizeMB = 10;
			if (htmlFile.size > 1000 * 1000 * maxFileSizeMB) {
				onHtmlFileSizeExceeded();
				return null;
			}
			try {
				showLoader();
				const formData = new FormData();
				formData.append('file', htmlFile, htmlFile.name);
				const {
					data: { fileId, fileHash },
				} = await postMediaFile({ data: formData });
				changeBillTemplateFileData({ fileId, fileHash, fileName: htmlFile.name });
				callback(htmlFile.name);
			} catch (err) {
				const error = err as Error;
				const appError = createAppError(error);
				addAppError(appError);
			} finally {
				hideLoader();
			}
		},
		[onHtmlFileSizeExceeded, changeBillTemplateFileData],
	);

	const getName = useCallback(() => {
		if (billAdvertisingImage && !billAdvertisingImageData.fileName) {
			const fileExtension = billAdvertisingImage.split('.').pop();
			return <Text translationKey={`image.${fileExtension}`} theme="secondary" size="s" />;
		}
		if (billAdvertisingImageData.fileName) {
			return (
				<Text
					translationKey={billAdvertisingImageData.fileName}
					theme="secondary"
					size="s"
					withTooltip={true}
				/>
			);
		}
		return <Text translationKey="noImage" translationNameSpace="form" theme="secondary" size="s" />;
	}, [billAdvertisingImage, billAdvertisingImageData.fileName]);

	const downloadImage = useCallback(() => {
		billAdvertisingImage && blobDownload();
	}, [blobDownload, billAdvertisingImage]);

	const deleteImage = useCallback(() => {
		(billAdvertisingImage || billAdvertisingImageData.fileId) && onDeleteBillAdvertisingImage();
	}, [onDeleteBillAdvertisingImage, billAdvertisingImage, billAdvertisingImageData.fileId]);

	return (
		<div className={style.container}>
			<div>
				<Text
					translationNameSpace="form"
					translationKey="billTemplateHeading"
					theme="secondary"
					size="l"
					weight="bold"
					display="inline"
				/>
				<div className={style.htmlUploader}>
					<FileUploader
						fileType="html"
						onFileLoad={handleHtmlUpload}
						id="billTemplateFile"
						label="uploadHtmlLabel"
						errorMessage="invalidFileFormat"
						filePath={billTemplateFileData ? billTemplateFileData.fileName : null}
						width={250}
					/>
					{billTemplateFilePath && (
						<BlobDownloader
							filePath={billTemplateFilePath}
							fileExtension="html"
							fileName="bill_template"
							label="downloadHtmlLabel"
							width={250}
						/>
					)}
				</div>
			</div>
			<div>
				<div className={style.advertisingHeading}>
					<Text
						translationNameSpace="form"
						translationKey="adImageHeading"
						theme="secondary"
						size="l"
						weight="bold"
						display="inline"
					/>
					<TooltipForElement title={tForm('advertisementTooltip')}>
						<svg className={style.icon}>
							<use xlinkHref={`${breadcrumbs}#circleInfo`} />
						</svg>
					</TooltipForElement>
				</div>
				<div className={style.imageUploader}>
					<DragAndDropForFile
						changeFileData={changeBillAdvertisingImageData}
						onImageFileSizeExceeded={onImageFileSizeExceeded}
						additionalDescription="PngJpgOrJpeg(recommendSize)"
						accept={allowedExtensions}
					/>
					<div className={style.controls}>
						<div>
							<div className={style.iconWrapper}>
								<svg className={style.icon}>
									<use xlinkHref={`${breadcrumbs}#file`} />
								</svg>
							</div>
							<div className={style.fileName}>{getName()}</div>
						</div>
						<div>
							<svg className={downloadIconClasses} onClick={downloadImage}>
								<use xlinkHref={`${breadcrumbs}#downloadArrow`} />
							</svg>
							<svg className={deleteIconClasses} onClick={deleteImage}>
								<use xlinkHref={`${breadcrumbs}#bin`} />
							</svg>
						</div>
					</div>
				</div>
				<FormInput
					testId="billAdvertisingLinkField"
					name="billAdvertisingLink"
					label="advertisingLinkLabel"
					onChange={changeBillAdvertisingLink}
					validationRules={validationRules}
				/>
			</div>
		</div>
	);
};

export default React.memo(BillTemplateBlock);
